/**
 * Shared building blocks for the dashboard's admin inline-edit mode.
 *
 * Two hard rules live here as helpers so every tab obeys them:
 *   - `trackScalarVars` — track edits always resend the FULL pipeline scalar
 *     set (admin_update_track plain-assigns them). Callers spread this and
 *     override the one field they're changing.
 *   - `weekStatusBody` — weekly-status edits resend the COMPLETE status body
 *     (upsertWeeklyStatus is full-replace); missing weeks fall back to defaults.
 *
 * The option sets mirror track-form-dialog.tsx to keep the vocabularies DRY.
 */

import { useEffect, useState } from "react";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { type DashboardTrack, type SourceRef, type WeeklyStatus } from "@/lib/api";
import { getSteerco } from "@/lib/steerco";
import { cn } from "@/lib/utils";

// ─── Sentinels for nullable selects ─────────────────────────────────────────
export const NOT_SET = "__unset__";
export const NO_OUTCOME = "__none__";
export const NO_PHASE = "__none__";

export interface Option {
  value: string;
  label: string;
}

export const STAGE_OPTIONS: Option[] = [
  { value: "not_started", label: "Not started" },
  { value: "discover", label: "Discover" },
  { value: "prioritize", label: "Prioritize" },
  { value: "prototype", label: "Prototype" },
  { value: "board_prep", label: "Board Prep" },
  { value: "paused", label: "Paused" },
  { value: "blocked", label: "Blocked" },
];

export const PROTOTYPE_RAG_OPTIONS: Option[] = [
  { value: NOT_SET, label: "— (not set)" },
  { value: "green", label: "Green" },
  { value: "amber", label: "Amber" },
  { value: "red", label: "Red" },
];

export const VALUE_CONFIDENCE_OPTIONS: Option[] = [
  { value: NOT_SET, label: "— (not set)" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
  { value: "tbc", label: "TBC" },
];

export const BOARD_READINESS_OPTIONS: Option[] = [
  { value: NOT_SET, label: "— (not set)" },
  { value: "not_started", label: "Not started" },
  { value: "early", label: "Early" },
  { value: "in_progress", label: "In progress" },
  { value: "ready", label: "Ready" },
  { value: "at_risk", label: "At risk" },
  { value: "blocked", label: "Blocked" },
];

export const BOARD_OUTCOME_OPTIONS: Option[] = [
  { value: NO_OUTCOME, label: "— none" },
  { value: "on_track", label: "On track" },
  { value: "monitor", label: "Monitor" },
  { value: "at_risk", label: "At risk" },
  { value: "blocked", label: "Blocked" },
];

/** Phases available for a per-week journey pill (with a clear option). */
export const PHASE_OPTIONS: Option[] = [
  { value: NO_PHASE, label: "— clear" },
  { value: "discover", label: "Discover" },
  { value: "prioritize", label: "Prioritize" },
  { value: "prototype", label: "Prototype" },
  { value: "board_prep", label: "Board Prep" },
  { value: "paused", label: "Paused" },
  { value: "blocked", label: "Blocked" },
];

/** Resolve a select sentinel back to a nullable API value. */
export function resolveNullable(value: string): string | null {
  return value === NOT_SET || value === NO_OUTCOME || value === NO_PHASE ? null : value;
}

// ─── Program vs. champion lanes ─────────────────────────────────────────────

/**
 * A `program` track is the company-wide big-picture lane (e.g. "Project
 * Espresso — Program"); every other track is a champion BU row. Anything that
 * isn't explicitly `program` counts as a champion so legacy rows with no `kind`
 * render exactly as before.
 */
export function isProgramTrack(track: DashboardTrack): boolean {
  return track.kind === "program";
}

/**
 * Split tracks into program lanes (pinned first) and champion rows. When no
 * program track exists, `programTracks` is empty and `championTracks` holds
 * every track — so the tabs render exactly as they did before `kind` existed.
 */
export function splitByKind(tracks: DashboardTrack[]): {
  programTracks: DashboardTrack[];
  championTracks: DashboardTrack[];
} {
  return {
    programTracks: tracks.filter(isProgramTrack),
    championTracks: tracks.filter((t) => !isProgramTrack(t)),
  };
}

/** Left-accent + subtle tint marking a row as the pinned program lane. */
export const PROGRAM_ROW_ACCENT = "border-primary bg-primary/5 border-l-4";

/** Small navy "Program" tag for the program lane's label cell. */
export function ProgramTag() {
  return (
    <span className="bg-boi-dark inline-block rounded-sm px-1.5 py-0.5 text-[9px] font-semibold tracking-wide text-white uppercase">
      Program
    </span>
  );
}

// ─── Payload builders (the two hard rules) ──────────────────────────────────

/**
 * The FULL pipeline scalar set for `useAdminUpdateTrack`, read from the current
 * track. Spread this and override the single field being edited so we never
 * blank out the others (admin_update_track plain-assigns each scalar).
 */
export function trackScalarVars(track: DashboardTrack) {
  return {
    trackId: track.id,
    stage: track.stage ?? "not_started",
    championNote: track.champion_note ?? null,
    useCasesIdentified: track.use_cases_identified ?? null,
    useCasesPrioritized: track.use_cases_prioritized ?? null,
    prototypeNote: track.prototype_note ?? null,
    prototypeRag: track.prototype_rag ?? null,
    valueConfidence: track.value_confidence ?? null,
    boardReadiness: track.board_readiness ?? null,
    boardOutcome: track.board_outcome ?? null,
    selectedUseCase: track.selected_use_case ?? null,
    // Preserve the exec Steerco snapshot on every scalar edit so unrelated
    // pipeline/pulse edits never blank it out. Steerco-tab edits spread this
    // and override the single field they change.
    steerco: getSteerco(track),
  };
}

/**
 * Build the next `phase_timeline` for a track: replace the entry for `week`
 * with `phase`, or drop it when `phase` is null. Sorted by week for stability.
 */
export function setPhaseInTimeline(
  track: DashboardTrack,
  week: string,
  phase: string | null,
): { week_start: string; phase: string }[] {
  const rest = (track.phase_timeline ?? []).filter((e) => e.week_start !== week);
  if (phase) {
    rest.push({ week_start: week, phase });
  }
  return rest.sort((a, b) => a.week_start.localeCompare(b.week_start));
}

/**
 * The COMPLETE weekly-status body for `useUpsertStatus`. Reads the current
 * week's status (if any) so a single-field edit resends everything else
 * unchanged; a brand-new week starts from safe defaults.
 */
export function weekStatusBody(
  trackId: string,
  weekStart: string,
  status: WeeklyStatus | undefined,
) {
  return {
    trackId,
    weekStart,
    rag: status?.rag ?? "green",
    percentComplete: status?.percent_complete ?? 0,
    summary: status?.summary ?? null,
    progress: status?.progress ?? null,
    blockers: status?.blockers ?? null,
    nextSteps: status?.next_steps ?? null,
    source: status?.source ?? "manual",
    sourceRefs: (status?.source_refs ?? []) as SourceRef[],
  };
}

// ─── Reusable inline controls ───────────────────────────────────────────────

interface InlineTextProps {
  value: string;
  onSave: (value: string) => void;
  placeholder?: string;
  type?: "text" | "number";
  className?: string;
}

/** Single-line editable field. Saves on blur (and Enter) only when changed. */
export function InlineText({
  value,
  onSave,
  placeholder,
  type = "text",
  className,
}: InlineTextProps) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (draft !== value) {
      onSave(draft);
    }
  };

  return (
    <Input
      value={draft}
      type={type}
      min={type === "number" ? 0 : undefined}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
      className={cn("h-7 px-2 py-1 text-xs", className)}
    />
  );
}

interface InlineTextareaProps {
  value: string;
  onSave: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
}

/** Multi-line editable field (newline = one bullet). Saves on blur when changed. */
export function InlineTextarea({
  value,
  onSave,
  placeholder,
  rows = 3,
  className,
}: InlineTextareaProps) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    if (draft !== value) {
      onSave(draft);
    }
  };

  return (
    <Textarea
      value={draft}
      rows={rows}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      className={cn("min-h-0 text-xs", className)}
    />
  );
}

interface InlineSelectProps {
  value: string;
  options: Option[];
  onSave: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/** Dropdown editable field. Saves on change. */
export function InlineSelect({
  value,
  options,
  onSave,
  placeholder,
  className,
}: InlineSelectProps) {
  return (
    <Select value={value} onValueChange={onSave}>
      <SelectTrigger className={cn("h-7 w-full text-xs", className)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} className="text-xs">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
